import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api, type AuditRow, type SpotCheckResult } from "../../lib/api";
import { queryKeys } from "../../lib/queryKeys";
import { formatInZone } from "../../lib/time";
import { Button } from "../ui/Button";
import { Select } from "../ui/Select";
import { cn } from "../ui/cn";

/**
 * The weekly team audit, and the ad-hoc one.
 *
 * **The week.** Tickets from last week per team, drawn once and then fixed, so
 * a manager can leave the page and come back to the same set -- or hand three
 * of them to somebody else. A sample that re-rolled on every load would not be
 * an audit. The draw covers every member of the team before it randomises
 * anything, so the quiet end of the desk stops being the end nobody reviews;
 * anybody it still could not reach is named underneath rather than silently
 * absent.
 *
 * **The spot check.** Five at random from whatever filter a manager sets, and
 * a different five every time they ask. That is the whole difference between
 * the two panels and the reason they are both here: the week is the review of
 * record, and this is the question you go and ask ("five of Sam's", "five
 * mentioning the courier") when something has prompted it.
 *
 * Its own page rather than a panel on the dashboard. The dashboard answers
 * "where is the desk right now" and gets read daily; this is a weekly review
 * of individual work, done by a different person for a different reason, and
 * folding it in made it look like one more chart to skim past.
 */

const FACES: Record<string, { emoji: string; label: string }> = {
  happy: { emoji: "🙂", label: "Happy" },
  neutral: { emoji: "😐", label: "Neutral" },
  angry: { emoji: "😠", label: "Angry" },
};

/** The windows the spot check offers, named as the report cards name them. */
const RANGES = [
  { value: "", label: "Any time" },
  { value: "this-month", label: "This month" },
  { value: "last-month", label: "Last month" },
  { value: "3-months", label: "3 months" },
  { value: "12-months", label: "12 months" },
] as const;

export function TeamAudit() {
  const [teamId, setTeamId] = useState<number | null>(null);

  const teams = useQuery({
    queryKey: ["audit-teams"],
    queryFn: api.auditTeams,
    staleTime: 5 * 60_000,
  });

  const audit = useQuery({
    queryKey: ["audit", teamId],
    queryFn: () => api.audit(teamId!),
    enabled: teamId !== null,
    /*
     * The first open of a week draws the sample and reads sentiment for five
     * tickets, so it is genuinely slow. Held for the session rather than
     * refetched on focus, because nothing about last week can change.
     */
    staleTime: 30 * 60_000,
  });

  return (
    <div className="h-full overflow-y-auto p-4">
      <header className="mb-4">
        <h1 className="text-base font-semibold text-content">Team audit</h1>
        <p className="mt-0.5 text-sm text-muted">
          Last week's tickets per team, drawn once so the same set is there when
          you come back — one from everybody, then at random.
        </p>
      </header>

      <div>
        {teams.isLoading ? (
          <p className="text-xs text-muted">Loading…</p>
        ) : (teams.data?.teams.length ?? 0) === 0 ? (
          <p className="text-xs text-muted">No teams are set up yet.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-1.5">
              {teams.data?.teams.map((team) => (
                <button
                  key={team.id}
                  type="button"
                  onClick={() => setTeamId(team.id === teamId ? null : team.id)}
                  aria-pressed={team.id === teamId}
                  className={cn(
                    "rounded-md border px-2.5 py-1 text-xs transition",
                    team.id === teamId
                      ? "border-brand-600 bg-brand-600 text-white"
                      : "border-line text-content hover:bg-surface-2",
                  )}
                >
                  {team.name}
                </button>
              ))}

              {teams.data ? (
                <span className="ml-auto text-[11px] text-faint">
                  Week of{" "}
                  {formatInZone(teams.data.weekStart, {
                    day: "numeric",
                    month: "short",
                  })}
                </span>
              ) : null}
            </div>

            {teamId === null ? (
              <p className="mt-3 text-xs text-muted">
                Choose a team to see its tickets.
              </p>
            ) : audit.isLoading ? (
              <p className="mt-3 text-xs text-muted">
                Drawing tickets and reading sentiment…
              </p>
            ) : (audit.data?.rows.length ?? 0) === 0 ? (
              <p className="mt-3 text-xs text-muted">
                Nobody on this team had a ticket raised last week.
              </p>
            ) : (
              <>
                <AuditTable rows={audit.data?.rows ?? []} />

                {/*
                  Named, not counted. The draw takes one ticket per member, so
                  anybody here had none raised to them last week -- which is
                  worth a manager's attention in its own right, and is the one
                  thing a sample cannot show by being looked at.
                */}
                {(audit.data?.uncovered.length ?? 0) > 0 ? (
                  <p className="mt-2 text-[11px] text-faint">
                    No ticket was raised to{" "}
                    {listed(audit.data!.uncovered.map((who) => who.name))} last
                    week, so there is nothing of theirs to review.
                  </p>
                ) : null}

                <SentimentNote />
              </>
            )}
          </>
        )}
      </div>

      <SpotCheck />
    </div>
  );
}

/**
 * Five at random from a filter, re-drawn on demand.
 *
 * A mutation rather than a query, and that is the right shape rather than a
 * technicality: pressing the button is meant to *change* the answer, where a
 * query's whole contract is that the same key gives the same data back. Keying
 * a query on the filters would cache a draw and hand back yesterday's five to
 * anybody who set the same filter twice, which is the one thing this panel must
 * not do.
 */
function SpotCheck() {
  const [userId, setUserId] = useState("");
  const [pipelineId, setPipelineId] = useState("");
  const [range, setRange] = useState<string>("3-months");
  const [search, setSearch] = useState("");

  const agents = useQuery({
    queryKey: queryKeys.users,
    queryFn: api.users,
    staleTime: 5 * 60_000,
  });

  const pipelines = useQuery({
    queryKey: queryKeys.pipelines,
    queryFn: api.pipelines,
    staleTime: 5 * 60_000,
  });

  const draw = useMutation<SpotCheckResult>({
    mutationFn: () =>
      api.auditSpotCheck({
        userId: userId ? Number(userId) : undefined,
        pipelineId: pipelineId ? Number(pipelineId) : undefined,
        search: search.trim() || undefined,
        range: range || undefined,
      }),
  });

  return (
    <section className="mt-8 border-t border-line pt-4">
      <h2 className="text-sm font-semibold text-content">Spot check</h2>
      <p className="mt-0.5 mb-3 text-xs text-muted">
        Five at random from whatever you narrow to. Nothing is kept — asking
        again gives a different five.
      </p>

      {/*
        A two-by-two grid on a phone, where the fields wrapped into a ragged
        staircase -- one select alone on the first line,
        two on the next, then the search box and the button. A desktop keeps
        the single wrapping row.
      */}
      <form
        className="grid grid-cols-2 items-center gap-1.5 md:flex md:flex-wrap"
        onSubmit={(event) => {
          event.preventDefault();
          draw.mutate();
        }}
      >
        <Select
          value={userId}
          onChange={setUserId}
          label="Whose tickets"
          className="max-md:h-8 max-md:w-full"
          options={[
            { value: "", label: "Anybody" },
            ...(agents.data ?? []).map((agent) => ({
              value: String(agent.id),
              label: agent.name,
            })),
          ]}
        />

        <Select
          value={pipelineId}
          onChange={setPipelineId}
          label="Which pipeline"
          className="max-md:h-8 max-md:w-full"
          options={[
            { value: "", label: "Any pipeline" },
            ...(pipelines.data ?? []).map((pipeline) => ({
              value: String(pipeline.id),
              label: pipeline.name,
            })),
          ]}
        />

        <Select
          value={range}
          onChange={setRange}
          label="Over what period"
          className="max-md:h-8 max-md:w-full"
          options={RANGES}
        />

        {/*
          The queue's own search, and the placeholder says so: this box runs
          `searchCondition` on the server, so an order number or an address
          finds what it finds in the queue rather than only matching words in
          a subject.
        */}
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Containing…"
          aria-label="Words, an order number or an address the ticket contains"
          className={cn(
            "h-8 w-full min-w-0 flex-1 rounded-md border border-line bg-surface px-2 text-xs md:h-7 md:w-auto",
            "text-content placeholder:text-faint",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
          )}
        />

        <span className="col-span-2 justify-self-end">
          <Button type="submit" size="sm" disabled={draw.isPending}>
            {draw.isPending ? "Drawing…" : "Draw five"}
          </Button>
        </span>
      </form>

      {draw.isError ? (
        <p className="mt-3 text-xs text-sla-breached">
          That draw failed. Try again, or narrow it further.
        </p>
      ) : draw.data && draw.data.rows.length === 0 ? (
        <p className="mt-3 text-xs text-muted">
          Nothing matches that. Widen the period or clear a filter.
        </p>
      ) : draw.data ? (
        <>
          <AuditTable rows={draw.data.rows} />
          <SentimentNote />
        </>
      ) : null}
    </section>
  );
}

/**
 * The review table both draws render.
 *
 * One component rather than two, because the columns are the review itself --
 * the ticket, whose it is, whether it was answered in time and how the customer
 * sounded. Two copies would have drifted the first time a column was added to
 * whichever panel somebody was looking at.
 */
function AuditTable({ rows }: { rows: AuditRow[] }) {
  return (
    <table className="mt-3 w-full text-xs">
      <thead>
        <tr className="border-b border-line text-left text-muted">
          <th className="py-1 font-medium">Ticket</th>
          <th className="py-1 font-medium">Assignee</th>
          <th className="py-1 font-medium">SLA</th>
          <th className="py-1 text-center font-medium">Sentiment</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.ticketId} className="border-b border-line/50">
            <td className="py-1.5 pr-2">
              <Link
                to={`/v/open/${row.ticketId}`}
                className="text-brand-600 hover:underline dark:text-brand-400"
              >
                #{row.ticketId}
              </Link>{" "}
              <span className="text-content">{row.subject}</span>
            </td>
            <td className="py-1.5 pr-2 text-muted">
              {row.assignee?.name ?? "Unassigned"}
            </td>
            <td className="py-1.5 pr-2">
              {row.missedSla ? (
                <span className="text-sla-breached">Missed</span>
              ) : (
                <span className="text-sla-ok">Met</span>
              )}
            </td>
            <td className="py-1.5 text-center">
              {row.sentiment ? (
                <span
                  title={row.sentimentRationale ?? FACES[row.sentiment]?.label}
                >
                  <span aria-hidden>{FACES[row.sentiment]?.emoji}</span>
                  {/* The emoji alone is not an accessible label, and three
                      faces are easy to confuse. */}
                  <span className="sr-only">{FACES[row.sentiment]?.label}</span>
                </span>
              ) : (
                <span className="text-faint" title="Not read yet">
                  —
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/*
  Said out loud under every table, because a face in a column next to
  somebody's name will otherwise be read as a mark for that person. It is the
  customer's tone, and a customer can be furious about something the agent
  handled perfectly.
*/
function SentimentNote() {
  return (
    <p className="mt-2 text-[11px] text-faint">
      Sentiment is the customer's tone, read from the conversation — not a
      judgement of the reply. An angry customer and a well-handled ticket often
      go together.
    </p>
  );
}

/** "Priya", "Priya and Tom", "Priya, Tom and Sam". */
function listed(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}
